
import { defineComponent } from 'vue'

import ActiveUserMixin from '~/mixins/activeUserMixin'
import AutoOnboardMixin from '~/mixins/autoOnboardMixin'
import ConfigGeneratorMixin from '~/mixins/configGeneratorMixin'
import OwnerDetailMixin from '~/mixins/ownerDetailMixin'
import { RepoConfigAnalyzerMeta, RepoConfigInterface } from '~/store/repository/detail'

export default defineComponent({
  name: 'NewOnboardingTemplateModal',
  mixins: [ConfigGeneratorMixin, OwnerDetailMixin, AutoOnboardMixin, ActiveUserMixin],

  data() {
    return {
      title: '',
      description: '',
      showAnalyzerList: false,
      savingConfig: false
    }
  },

  computed: {
    selectedTransformers() {
      return this.templateConfig.code_formatters ?? this.templateConfig.transformers ?? []
    },
    baseConfig(): {
      title: string
      description: string
      config: string
    } {
      return {
        title: this.title,
        description: this.description,
        config: JSON.stringify(this.templateConfig)
      }
    }
  },

  watch: {
    '$route.path': {
      handler() {
        this.$emit('close')
      }
    }
  },

  methods: {
    async saveConfig(close: () => void): Promise<void> {
      let isDirty = false
      if (!this.title) {
        this.$toast.info('Please enter a title before submitting')
        isDirty = true
      }

      if (this.templateConfig.analyzers.length === 0) {
        this.$toast.info('Please add at least one analyzer before submitting')
        isDirty = true
      }

      if (isDirty) {
        return
      }

      this.savingConfig = true
      try {
        const res = await this.createConfigTemplate({
          ownerId: this.owner.id,
          ...this.baseConfig
        })
        await this.refetch(res.template?.shortcode)

        this.$toast.success('Config template saved successfully')
        this.$router.push(this.$generateRoute(['settings', 'auto-onboard'], false, true))
        close()
      } catch (e) {
        this.$toast.danger(
          'There was a problem saving this config, please check your config for errors or contact support.'
        )
        this.logErrorForUser(e as Error, 'Auto Onboard template creation', this.baseConfig)
      } finally {
        this.savingConfig = false
      }
    },
    async refetch(shortcode?: string): Promise<void> {
      await this.refetchTemplateList(this.activeProvider, this.activeOwner)
      if (shortcode) {
        await this.fetchSingleTemplate({
          login: this.activeOwner,
          provider: this.activeProvider,
          shortcode,
          refetch: true
        })
      }
    },
    getConfig(shortcode: string): RepoConfigAnalyzerMeta {
      if (this.configTemplate) {
        const config = JSON.parse(this.configTemplate.config) as RepoConfigInterface
        const filteredList = config.analyzers.filter((analyzer) => analyzer.name === shortcode)
        return filteredList.length ? filteredList[0] : ({} as RepoConfigAnalyzerMeta)
      }
      return {} as RepoConfigAnalyzerMeta
    }
  }
})
